import React from "react";
import { useNavigate } from "react-router-dom";
import settings from "./settings";

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const WEEKDAY_LABELS = ["Nie: ", "Pon: ", "Wt: ", "Śr: ", "Czw: ", "Pt: ", "Sob: "];
const HOURS_BY_INDEX = [
  { x: "Ndz", y: "sunday" },
  { x: "Pon", y: "monday" },
  { x: "Wt", y: "tuesday" },
  { x: "Śr", y: "wednesday" },
  { x: "Czw", y: "thursday" },
  { x: "Pt", y: "friday" },
  { x: "Sob", y: "saturday" },
];
const DAY_NAMES = ["Niedziela", "Poniedziałek", "Wtorek", "Środa", "Czwartek", "Piątek", "Sobota"];
const MONTH_NAMES_SECONDARY = [
  "Stycznia", "Luty", "Marca", "Kwietnia", "Maja", "Czerwca",
  "Lipca", "Sierpnia", "Września", "Października", "Listopada", "Grudnia",
];
const MAX_STARS = 5;

function getPlaceTags(place) {
  return place?.tags?.map((tag) => tag.name) ?? [];
}

export function getTodayHours(place) {
  if (!place?.hours) {
    return {};
  }

  const day = new Date().getDay();
  const weekDay = WEEKDAY_LABELS[day] ?? "";
  const searchDay = place.hours.find((item) => item.day === WEEKDAYS[day]);

  if (!searchDay) {
    return { weekDay, open: "", close: "nieczynne" };
  }

  return {
    weekDay,
    open: searchDay.open,
    close: searchDay.close,
  };
}

export function getPlaceHoursByDay(place, day, t = null) {
  const hours = { weekDay: "", open: "", close: "" };

  if (place === undefined || day === undefined) {
    return hours;
  }

  const tags = getPlaceTags(place);

  if (tags.includes("kościoły")) {
    hours.open = t ? t("churchHours") : "według mszy i nabożeństw";
    return hours;
  }

  if (tags.includes("teatry") || tags.includes("kina")) {
    hours.open = t ? t("scheduleHours") : "według repertuaru";
    return hours;
  }

  const target = place.hours?.find((item) => item.day === HOURS_BY_INDEX[day].y);
  if (!target) {
    return hours;
  }

  return {
    weekDay: HOURS_BY_INDEX[day].x,
    open: target.open,
    close: target.close,
  };
}

export function getShortDate(date) {
  if (!date) {
    return "";
  }

  const tempDay = new Date(date);
  const day = String(tempDay.getDate()).padStart(2, "0");
  const month = String(tempDay.getMonth() + 1).padStart(2, "0");

  return `${day}.${month}.${tempDay.getFullYear()}`;
}

export function getDayNameByNumber(number) {
  return DAY_NAMES[number] ?? "none";
}

export function getDayName(day, month, year) {
  const currentDay = new Date();
  currentDay.setDate(day);
  currentDay.setMonth(typeof month === "string" && month.length > 1 && month[0] === "0" ? month[1] : month);
  currentDay.setFullYear(year);
  return DAY_NAMES[currentDay.getDay()] ?? "none";
}

export function getSecondaryMonthName(month) {
  return MONTH_NAMES_SECONDARY[month] ?? "none";
}

export function getEventStartDate(event) {
  const value = event?.startsAt ?? event?.startDate;
  if (!value) return null;

  // Legacy data is a date-only YYYY-MM-DD string. Parse it at local noon so
  // formatting never shifts the calendar day because of timezone conversion.
  const date = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00`)
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getEventDate(event) {
  const date = getEventStartDate(event);
  if (!date) return "";
  return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.${date.getFullYear()}`;
}

export function getEventDayName(event) {
  const date = getEventStartDate(event);
  return date ? DAY_NAMES[date.getDay()] ?? "" : "";
}

export function getEventHours(event) {
  return event.hours?.[0]?.open ?? "";
}

export function getPhone(place) {
  return place.phone ?? "-";
}

export function getAddress(place) {
  return place.address ?? "-";
}

export function getSortedTags(place) {
  if (!place?.tags?.length) {
    return;
  }

  return [...place.tags];
}

export function getPlaceStarsRating(commentsAndRatings) {
  if (!commentsAndRatings?.rating || commentsAndRatings.rating <= 0) {
    return;
  }

  return getStarsString(commentsAndRatings.rating);
}

export function getStarsString(value) {
  if (value <= 0 || value > MAX_STARS) {
    return;
  }

  const fullStars = Math.floor(value);
  const hasHalfStar = value % 1 !== 0;
  const stars = [];
  for (let i = 0; i < MAX_STARS; i++) {
    stars.push(i < fullStars ? 1 : i === fullStars && hasHalfStar ? 2 : 0);
  }

  return stars;
}

export function getStarsImages(array) {
  return array.map((item, index) => (
    <img
      key={index}
      alt=""
      src={process.env.PUBLIC_URL + (item === 0 ? "/icons/star-empty-white.png" : item === 1 ? "/icons/star-full.png" : "/icons/star-half.png")}
    />
  ));
}

export function getRating(commentsAndRatings, reviews) {
  if (!commentsAndRatings?.length) {
    return;
  }

  let value = 0;
  let index = 0;

  commentsAndRatings.forEach((item) => {
    value += item.rating;
    index++;
  });

  if (reviews?.length) {
    reviews.forEach((item) => {
      value += item.rating;
      index++;
    });
  }

  const finalRating = Math.round((value / index + Number.EPSILON) * 100) / 100;

  return {
    finalRating,
    votes: index,
    stars: getStarsString(finalRating),
  };
}

export function getPlaceHoursString(place, t = null) {
  const tags = getPlaceTags(place);

  if (tags.includes("kościoły")) {
    return t ? t("churchHours") : "według mszy i nabożeństw";
  }

  if (tags.includes("teatry") || tags.includes("kina")) {
    return t ? t("scheduleHours") : "według repertuaru";
  }

  if (checkIsOpen(place) === false && (!getPlaceHoursByDay(place, new Date().getDay(), t).open)) {
    return t ? t("closed") : "nieczynne";
  }

  if (checkIsWholeTimeOpen(place, new Date().getDay())) {
    return t ? t("allDay") : "całą dobę";
  }

  const hours = getTodayHours(place);
  return hours.weekDay + (hours.open === "" ? "" : hours.open + " - ") + hours.close;
}

export function checkIsOpen(place) {
  const placeHours = getTodayHours(place);
  const dateNow = new Date();
  const tags = getPlaceTags(place);

  if (tags.includes("kościoły") || tags.includes("teatry") || tags.includes("kina")) {
    return true;
  }

  if (placeHours.open === "" || placeHours.close === "") {
    return false;
  }

  if (checkIsWholeTimeOpen(place, new Date().getDay())) {
    return true;
  }

  return dateNow.getHours() >= placeHours.open.split(":")[0] && dateNow.getHours() < placeHours.close.split(":")[0];
}

export function checkIsWholeTimeOpen(place, day) {
  return getPlaceHoursByDay(place, day).open === "00:00" && getPlaceHoursByDay(place, day).close === "00:00";
}

export function BackLink(props) {
  const navigate = useNavigate();
  return (
    <button className="btn btn-outline-primary" onClick={() => navigate(-1)}>
      {props.content}
    </button>
  );
}

export function refreshUserLocation() {
  if (!navigator.geolocation) {
    return;
  }

  navigator.geolocation.getCurrentPosition((position) => {
    const positionString = `${position.coords.latitude},${position.coords.longitude}`;
    if (positionString !== settings.userLocation) {
      settings.userLocation = positionString;
    }
  });
}

function getDistanceFromLatLng(lat1, lon1, lat2, lon2) {
  const R = 6378137;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

export function getLatLng(location) {
  if (!location) {
    return undefined;
  }

  if (typeof location === "string") {
    return {
      lat: parseFloat(location.split(",")[0]),
      lng: parseFloat(location.split(",")[1]),
    };
  }

  return location;
}

export function getDistanceBetweenLocations(source, destination) {
  const sourceLocation = getLatLng(source);
  const destinationLocation = getLatLng(destination);

  if (!sourceLocation || !destinationLocation) {
    return undefined;
  }

  return getDistanceFromLatLng(
    sourceLocation.lat,
    sourceLocation.lng,
    destinationLocation.lat,
    destinationLocation.lng
  );
}

export function getDistanceByUserLocation(destination) {
  if (!settings.userLocation) {
    return;
  }

  return getDistanceBetweenLocations(settings.userLocation, destination);
}
